/**
 * Board contract for the Recurring Job items surface.
 *
 * `params` is the host bag every board affordance reads — row commands, the create dialog and the
 * bulk fan-out all resolve it through `resolveBoardParams`. Signals, not values: the mount rebuilds
 * its inputs whenever the recurring job record changes, and the board must see the new scope without
 * being re-created.
 */
import { ActivityEntityName } from '@jms/model';
import type { ItemBrowserAddAdapter } from '@workbuddy/entities/shared/item.browser.dialog';
import type { EmbeddedItemHostParams } from '@workbuddy/surfaces/embedded-item';
import type { EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board';

/**
 * The item grain of this board: where one row's item sits inside the parent-shaped recurring job.
 *
 * One const because two things must agree — the board's `restrictions.recordScopes` and the walker
 * that reaches into a row for its item.
 */
export const RECURRING_JOB_TEMPLATE_ITEM_SCOPE = 'jobTemplate.template.workOrders.codes';

/** Board rows cache key. Every write invalidates it, so the two commands must not spell it apart. */
export const RECURRING_JOB_TEMPLATE_ITEM_QUERY_KEY = [ActivityEntityName.RecurringJobTemplateItem, 'items'];

export interface RecurringJobTemplateItemBoardParams extends EmbeddedItemHostParams {
  /**
   * Owning recurring job. Every read scope and every write command is addressed by it.
   *
   * `turnOn` rides along because Delete needs it: a running recurring job may not be left with zero
   * items, or the next generated job comes out empty.
   */
  readonly recurringJob: () => { _id: string; name?: string; turnOn?: boolean } | null;
  /** Items on the template right now — the delete guard counts against it. */
  readonly itemCount: () => number;
  /**
   * Tenant setting `items.displayAdditionalLineFields`. When true the money cells lead with the line
   * extension over the unit price and their headers read "EXT"; when false they show the unit alone.
   */
  readonly showAdditionalLineFields: () => boolean;
  /** False while the generator is running or the user lacks Manage — Add / Edit / Delete stay hidden. */
  readonly writable: () => boolean;
  /** Host refresh after any mutation; the recurring job record carries the items it just changed. */
  readonly itemsChanged: () => void;
  /**
   * Builds this mount's half of the shared catalogue create dialog — wording, write scope and the
   * write itself. Supplied by the manifest; the dialog reads it
   * off `params` because a custom create operation has no per-entity provider hook.
   */
  readonly itemBrowser?: () => ItemBrowserAddAdapter;
}

export type RecurringJobTemplateItemBoardContext = EntityBoardContext & {
  readonly params: RecurringJobTemplateItemBoardParams;
};
